import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '../shared';
import { AuthUser } from '../../types';

interface HeaderProps {
  authUser?: AuthUser | null;
  isAuthenticated?: boolean;
  onLogout?: () => Promise<void>;
  onShowAuthModal?: () => void;
}

export const Header = ({
  authUser,
  isAuthenticated,
  onLogout,
  onShowAuthModal
}: HeaderProps) => {
  const location = useLocation();
  const displayName = authUser?.nickname || authUser?.email || 'Foodbook';
  const avatarInitial = displayName.trim().charAt(0).toUpperCase() || 'F';
  
  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `text-fluid-sm font-bold px-4 py-1.5 flex items-center gap-2 cursor-pointer transition-colors ${
      isActive
        ? 'nav-item-active rounded-md jagged-corners'
        : 'text-[#fffaf0]/80 hover:text-primary'
    }`;

  const isProfileActive = location.pathname === '/profile' || location.pathname === '/devadmin';
  const navItems: Array<{ to: string; label: string; icon: string; end?: boolean }> = [
    { to: '/', label: '计划', icon: 'calendar_month', end: true },
    { to: '/recipes', label: '食谱', icon: 'menu_book' },
    { to: '/ingredients', label: '食材', icon: 'nutrition' },
    { to: '/shopping', label: '清单', icon: 'checklist' },
  ];

  return (
    <>
    <header className="shrink-0 z-50 w-full bg-wood-dark dark:bg-[#3e2723] text-white border-b-4 border-wood-darker shadow-pixel">
      <div className="page-container flex h-full grow flex-col mx-auto">
        <div className="flex items-center justify-between px-3 py-3 md:px-10">
          <NavLink to="/" className="flex min-w-0 items-center gap-3 md:gap-4">
            <div className="size-12 shrink-0 overflow-visible md:size-14">
              <img
                src="/icons/foodbook-egg-192.png"
                alt="Foodbook logo"
                className="h-full w-full object-contain drop-shadow-[0_2px_0_rgba(62,39,35,0.25)]"
              />
            </div>
            <div>
              <h2 className="text-fluid-xl font-black leading-tight tracking-tight text-[#fffaf0] drop-shadow-md">做饭百科</h2>
              <p className="text-fluid-xs text-[#deb887] font-medium">Foodbook</p>
            </div>
          </NavLink>
          
          <nav className="hidden md:flex items-center gap-2 bg-[#6d4621] p-1 rounded-lg border-2 border-wood-darker shadow-pixel-inset jagged-corners">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={navItemClass} end={item.end}>
                <Icon name={item.icon} className="text-fluid-sm" /> {item.label}
              </NavLink>
            ))}
          </nav>
          
          <div className="flex items-center gap-4">
            {isAuthenticated && authUser ? (
              <NavLink
                to="/profile"
                className={`group relative flex max-w-[54vw] items-center gap-2 rounded-full border-2 border-wood-darker bg-[#6d4621] p-1 pr-3 transition-colors hover:bg-wood-darker jagged-corners md:max-w-none md:gap-3 md:pr-4 ${
                  isProfileActive ? 'ring-2 ring-primary' : ''
                }`}
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#f7c873] font-black text-[#4e2c12]">
                  {avatarInitial}
                </div>
                <div className="flex min-w-0 flex-col items-start leading-none">
                  <span className="truncate text-fluid-xs font-black text-[#fffaf0] transition-colors group-hover:text-primary">{displayName}</span>
                  <span className="origin-left scale-75 text-fluid-xs font-bold text-primary">Lv.1 新人小厨</span>
                </div>
              </NavLink>
            ) : (
              <button
                onClick={onShowAuthModal}
                className="px-4 py-2 bg-primary text-black font-bold rounded border-2 border-white shadow-pixel-sm hover:bg-primary/90 transition-colors jagged-corners flex items-center gap-2"
              >
                <Icon name="login" className="text-fluid-sm" />
                <span>登录</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
    <nav
      className="fixed left-3 right-3 z-50 rounded-2xl border-4 border-wood-darker bg-[#6d4621]/95 px-2 py-2 shadow-pixel backdrop-blur md:hidden"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
    >
      <div className="grid grid-cols-5 gap-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[11px] font-black transition-colors ${
                isActive
                  ? 'bg-primary text-[#3e2723]'
                  : 'text-[#fffaf0]/80 hover:text-white'
              }`
            }
          >
            <Icon name={item.icon} className="text-lg" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[11px] font-black transition-colors ${
              isActive || isProfileActive
                ? 'bg-primary text-[#3e2723]'
                : 'text-[#fffaf0]/80 hover:text-white'
            }`
          }
        >
          <div className="flex size-5 items-center justify-center rounded-full bg-[#f7c873] text-[10px] font-black text-[#4e2c12]">
            {avatarInitial}
          </div>
          <span>我的</span>
        </NavLink>
      </div>
    </nav>
    </>
  );
};
